"use client";

import { useState, useEffect } from "react";
import {
  Music,
  Trophy,
  Vote,
  Gamepad2,
  Bitcoin,
  Star,
  Sparkles,
  TrendingUp,
  ExternalLink,
  Users,
  Globe,
  Zap,
  MessageCircle,
  BarChart3,
  Clock,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";
import { ContextAnalysis, getThemeColors } from "@/hooks/useAIContextAnalysis";

interface AIContextSidebarProps {
  analysis: ContextAnalysis;
  eventTitle: string;
  marketQuestions?: string[];
}

// Icon mapping
function getIcon(iconName?: string, className?: string) {
  const props = { className: className || "w-4 h-4" };
  switch (iconName) {
    case "music":
      return <Music {...props} />;
    case "trophy":
      return <Trophy {...props} />;
    case "vote":
      return <Vote {...props} />;
    case "gamepad":
      return <Gamepad2 {...props} />;
    case "bitcoin":
      return <Bitcoin {...props} />;
    case "star":
      return <Star {...props} />;
    case "twitter":
      return <MessageCircle {...props} />;
    default:
      return <Sparkles {...props} />;
  }
}

// Entity card component for displaying entities
function EntityCard({
  entity,
  index,
  theme,
  selected,
  onClick,
}: {
  entity: ContextAnalysis["entities"][0];
  index: number;
  theme: ReturnType<typeof getThemeColors>;
  selected: boolean;
  onClick: () => void;
}) {
  const rankColors = ["text-[#FFD700]", "text-[#C0C0C0]", "text-[#CD7F32]", "text-gray-500"];

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-2.5 rounded-[5px] transition-colors ${
        selected
          ? `bg-${theme.text}/20 border border-${theme.text}/50`
          : "bg-[#18181D]/30 hover:bg-[#18181D]/50"
      }`}
    >
      <span className={`w-6 text-center font-monument font-bold text-sm ${rankColors[index] || rankColors[3]}`}>
        {index + 1}
      </span>
      <div className="w-10 h-10 rounded-[5px] bg-[#18181D] flex items-center justify-center flex-shrink-0">
        {entity.type === "song" && <Music className="w-5 h-5 text-gray-400" />}
        {entity.type === "artist" && <Users className="w-5 h-5 text-gray-400" />}
        {entity.type === "team" && <Trophy className="w-5 h-5 text-gray-400" />}
        {entity.type === "player" && <Users className="w-5 h-5 text-gray-400" />}
        {entity.type === "candidate" && <Vote className="w-5 h-5 text-gray-400" />}
        {entity.type === "game" && <Gamepad2 className="w-5 h-5 text-gray-400" />}
        {entity.type === "crypto" && <Bitcoin className="w-5 h-5 text-gray-400" />}
        {entity.type === "person" && <Users className="w-5 h-5 text-gray-400" />}
        {entity.type === "other" && <Sparkles className="w-5 h-5 text-gray-400" />}
      </div>
      <div className="flex-1 text-left min-w-0">
        <p className="text-white font-satoshi font-bold text-sm truncate">{entity.name}</p>
        {entity.metadata && Object.keys(entity.metadata).length > 0 && (
          <p className="text-gray-400 text-xs truncate">
            {Object.entries(entity.metadata)
              .slice(0, 2)
              .map(([, v]) => v)
              .join(" • ")}
          </p>
        )}
      </div>
      <TrendingUp className={`w-4 h-4 ${selected ? `text-${theme.text}` : "text-gray-500"}`} />
    </button>
  );
}

// Data source link component
function DataSourceLink({
  source,
  theme,
}: {
  source: ContextAnalysis["dataSources"][0];
  theme: ReturnType<typeof getThemeColors>;
}) {
  const apiLabels: Record<string, string> = {
    spotify: "Spotify",
    espn: "ESPN",
    twitter: "X/Twitter",
    coingecko: "CoinGecko",
    wikipedia: "Wikipedia",
    none: "",
  };

  const apiUrls: Record<string, string> = {
    spotify: "https://open.spotify.com",
    espn: "https://www.espn.com",
    twitter: "https://twitter.com",
    coingecko: "https://www.coingecko.com",
    wikipedia: "https://www.wikipedia.org",
  };

  if (source.api === "none") return null;

  return (
    <a
      href={apiUrls[source.api] || "#"}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-2 px-3 py-2 bg-[#18181D]/30 hover:bg-[#18181D]/50 rounded-[5px] transition-colors group`}
    >
      <Globe className="w-4 h-4 text-gray-400 group-hover:text-white" />
      <span className="text-gray-300 text-xs font-inter-tight group-hover:text-white">
        {apiLabels[source.api]}
      </span>
      <ExternalLink className="w-3 h-3 text-gray-500 group-hover:text-white ml-auto" />
    </a>
  );
}

export default function AIContextSidebar({ analysis, eventTitle, marketQuestions = [] }: AIContextSidebarProps) {
  const [selectedEntityIndex, setSelectedEntityIndex] = useState(0);
  const [showAllEntities, setShowAllEntities] = useState(false);
  const [enrichedData, setEnrichedData] = useState<Record<string, any>>({});
  const [loadingEnriched, setLoadingEnriched] = useState(false);

  const theme = getThemeColors(analysis.displayHints.theme);
  const selectedEntity = analysis.entities[selectedEntityIndex];

  // Fetch enriched data for the selected entity
  useEffect(() => {
    const fetchEnrichedData = async () => {
      if (!selectedEntity) return;

      // Check if we already have data for this entity
      if (enrichedData[selectedEntity.name]) return;

      setLoadingEnriched(true);

      try {
        let data = null;

        // Fetch based on entity type and context
        if (analysis.contextType === "spotify_songs" && selectedEntity.type === "song") {
          const artist = selectedEntity.metadata?.artist || "";
          const res = await fetch(
            `/api/spotify/track/search?track=${encodeURIComponent(selectedEntity.name)}&artist=${encodeURIComponent(artist)}`
          );
          if (res.ok) {
            data = await res.json();
          }
        } else if (analysis.contextType === "spotify_artists" && selectedEntity.type === "artist") {
          const searchRes = await fetch(`/api/spotify/search?q=${encodeURIComponent(selectedEntity.name)}`);
          if (searchRes.ok) {
            const searchData = await searchRes.json();
            if (searchData.id) {
              const artistRes = await fetch(`/api/spotify/artist/${searchData.id}`);
              if (artistRes.ok) {
                data = await artistRes.json();
              }
            }
          }
        }
        // Add more enrichment logic for other entity types as needed

        if (data) {
          setEnrichedData((prev) => ({
            ...prev,
            [selectedEntity.name]: data,
          }));
        }
      } catch (err) {
        // Silently fail enrichment - we'll show basic info
      } finally {
        setLoadingEnriched(false);
      }
    };

    fetchEnrichedData();
  }, [selectedEntity, analysis.contextType, enrichedData]);

  const displayEntities = showAllEntities ? analysis.entities : analysis.entities.slice(0, 5);
  const hasMoreEntities = analysis.entities.length > 5;
  const currentEnrichedData = selectedEntity ? enrichedData[selectedEntity.name] : null;

  return (
    <div className="space-y-4">
      {/* Main Rankings Card */}
      <div
        className={`bg-[#18181D] rounded-[5px] border border-[#1A1A1A] overflow-hidden`}
      >
        {/* Header */}
        <div
          className="px-4 py-3 flex items-center justify-between"
          style={{ background: theme.primary }}
        >
          <div className="flex items-center gap-2">
            {getIcon(analysis.displayHints.icon, "w-4 h-4 text-black")}
            <span className="text-black text-sm font-inter-tight font-bold">
              {analysis.widgetConfig.title}
            </span>
          </div>
          {analysis.widgetConfig.subtitle && (
            <span className="text-black/70 text-xs font-inter-tight">
              {analysis.widgetConfig.subtitle}
            </span>
          )}
        </div>

        {/* Entity List */}
        {analysis.displayHints.showLeaderboard && analysis.entities.length > 0 && (
          <div className="p-3 space-y-2 max-h-[350px] overflow-y-auto scrollbar-hide">
            {displayEntities.map((entity, index) => (
              <EntityCard
                key={`${entity.name}-${index}`}
                entity={entity}
                index={index}
                theme={theme}
                selected={selectedEntityIndex === index}
                onClick={() => setSelectedEntityIndex(index)}
              />
            ))}

            {/* Show more/less button */}
            {hasMoreEntities && (
              <button
                onClick={() => setShowAllEntities(!showAllEntities)}
                className="w-full flex items-center justify-center gap-1 py-2 text-gray-400 hover:text-white text-xs font-inter-tight transition-colors"
              >
                {showAllEntities ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Show {analysis.entities.length - 5} more
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Confidence indicator */}
        <div className="px-3 pb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-gray-400 text-[10px] font-inter-tight">AI Confidence</span>
            <span className="text-gray-300 text-[10px] font-inter-tight font-bold">
              {analysis.confidence}%
            </span>
          </div>
          <div className="h-1 bg-[#18181D] rounded-[5px] overflow-hidden">
            <div
              className="h-full transition-[width] duration-500"
              style={{
                width: `${analysis.confidence}%`,
                background: theme.primary,
              }}
            />
          </div>
        </div>
      </div>

      {/* Selected Entity Details */}
      {selectedEntity && (
        <div
          className="bg-[#18181D] rounded-[5px] border border-[#1A1A1A] overflow-hidden"
        >
          {/* Entity Header */}
          <div className="p-4 border-b border-[#1A1A1A]/50">
            <div className="flex items-start gap-3">
              {currentEnrichedData?.album?.image || currentEnrichedData?.image ? (
                <img
                  src={currentEnrichedData.album?.image || currentEnrichedData.image}
                  alt={selectedEntity.name}
                  className="w-16 h-16 rounded-[5px] object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-[5px] bg-[#18181D] flex items-center justify-center">
                  {getIcon(analysis.displayHints.icon, "w-8 h-8 text-gray-400")}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-monument font-bold text-lg leading-tight truncate">
                  {selectedEntity.name}
                </h3>
                {selectedEntity.metadata?.artist && (
                  <p className="text-gray-400 text-sm">{selectedEntity.metadata.artist}</p>
                )}
                {selectedEntity.metadata?.league && (
                  <p className="text-gray-400 text-sm">{selectedEntity.metadata.league}</p>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className="px-2 py-0.5 text-[10px] font-inter-tight font-bold rounded-[5px]"
                    style={{ backgroundColor: `${theme.primary}20`, color: theme.primary }}
                  >
                    {selectedEntity.type.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Enriched Stats */}
          {loadingEnriched ? (
            <div className="p-4 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            </div>
          ) : currentEnrichedData && analysis.widgetConfig.showStats ? (
            <div className="p-4 space-y-3">
              {/* Spotify Track Stats */}
              {analysis.contextType === "spotify_songs" && currentEnrichedData.popularity !== undefined && (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-400 text-xs">
                        {analysis.widgetConfig.rankingMetric || "Popularity"}
                      </span>
                      <span className="text-gray-200 font-bold text-sm">
                        {currentEnrichedData.popularity}/100
                      </span>
                    </div>
                    <div className="h-2 bg-[#18181D] rounded-[5px] overflow-hidden">
                      <div
                        className="h-full transition-[width] duration-500"
                        style={{
                          width: `${currentEnrichedData.popularity}%`,
                          background: theme.primary,
                        }}
                      />
                    </div>
                  </div>

                  {currentEnrichedData.artistInfo && (
                    <div className="pt-2 border-t border-[#1A1A1A]/50">
                      <div className="flex items-center gap-2">
                        {currentEnrichedData.artistInfo.image && (
                          <img
                            src={currentEnrichedData.artistInfo.image}
                            alt="Artist"
                            className="w-8 h-8 rounded-[5px] object-cover"
                          />
                        )}
                        <div className="flex-1">
                          <p className="text-white text-xs font-bold">
                            {currentEnrichedData.artists?.[0]?.name}
                          </p>
                          <p className="text-gray-500 text-[10px]">
                            {formatNumber(currentEnrichedData.artistInfo.followers)} followers
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Spotify Artist Stats */}
              {analysis.contextType === "spotify_artists" && (
                <>
                  {currentEnrichedData.followers && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs flex items-center gap-1">
                        <Users className="w-3 h-3" /> Followers
                      </span>
                      <span className="text-white font-bold text-sm">
                        {formatNumber(currentEnrichedData.followers)}
                      </span>
                    </div>
                  )}
                  {currentEnrichedData.popularity !== undefined && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-gray-400 text-xs">Popularity</span>
                        <span className="text-gray-200 font-bold text-sm">
                          {currentEnrichedData.popularity}/100
                        </span>
                      </div>
                      <div className="h-2 bg-[#18181D] rounded-[5px] overflow-hidden">
                        <div
                          className="h-full transition-[width] duration-500"
                          style={{
                            width: `${currentEnrichedData.popularity}%`,
                            background: theme.primary,
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {currentEnrichedData.genres && currentEnrichedData.genres.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-2">
                      {currentEnrichedData.genres.slice(0, 3).map((genre: string) => (
                        <span
                          key={genre}
                          className="px-2 py-0.5 text-[10px] font-inter-tight rounded-[5px]"
                          style={{ backgroundColor: `${theme.primary}20`, color: theme.primary }}
                        >
                          {genre}
                        </span>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : null}

          {/* External Links */}
          {analysis.displayHints.showExternalLinks && currentEnrichedData?.spotifyUrl && (
            <div className="px-4 pb-4">
              <a
                href={currentEnrichedData.spotifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-[5px] font-inter-tight font-bold text-sm transition-colors text-black"
                style={{ background: theme.primary }}
              >
                {getIcon(analysis.displayHints.icon, "w-4 h-4")}
                Open in {getSourceName(analysis.dataSources[0]?.api)}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
        </div>
      )}

      {/* Data Sources */}
      {analysis.dataSources.length > 0 && analysis.dataSources[0].api !== "none" && (
        <div className="bg-[#18181D]/20 rounded-[5px] border border-[#1A1A1A] p-3">
          <h4 className="text-gray-400 text-[10px] font-inter-tight font-bold uppercase tracking-wider mb-2">
            Data Sources
          </h4>
          <div className="space-y-2">
            {analysis.dataSources.slice(0, 3).map((source, index) => (
              <DataSourceLink key={index} source={source} theme={theme} />
            ))}
          </div>
        </div>
      )}

      {/* AI Reasoning (collapsible) */}
      {analysis.reasoning && (
        <details className="group">
          <summary className="flex items-center gap-2 text-gray-500 text-xs font-inter-tight cursor-pointer hover:text-gray-300 transition-colors">
            <Zap className="w-3 h-3" />
            <span>Why this widget?</span>
          </summary>
          <p className="mt-2 text-gray-400 text-xs leading-relaxed pl-5">
            {analysis.reasoning}
          </p>
        </details>
      )}
    </div>
  );
}

// Utility functions
function formatNumber(num: number): string {
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(1)}B`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toString();
}

function getSourceName(api?: string): string {
  const names: Record<string, string> = {
    spotify: "Spotify",
    espn: "ESPN",
    twitter: "X",
    coingecko: "CoinGecko",
    wikipedia: "Wikipedia",
  };
  return names[api || ""] || "Source";
}
